import {useEffect, useState} from "react";
import {Button, Message, Toggle, toaster} from "rsuite";
import {TestApi} from "../../services/Test.api";
import {
  Common_DeviceDto,
  GetEspSystemData_GetEspSystemDataResponse,
  GetMeteoSourceBroadcastDevice_GetMeteoSourceBroadcastDeviceResponse,
} from "../../api";
import DevicesModal from "../modals/DeviceModal/DeviceModal";
import {DeviceListItem, DevicesResponse} from "../modals/modals.model";
import {SharedApi} from "../../services/Shared.api";
import "./TestComponent.scss";

type TestComponentProps = {
  deviceId: string;
};

const TestComponent = ({deviceId}: TestComponentProps) => {
  const [gpsUseCoords, setGpsUseCoords] = useState(false);
  const [gpsUseTime, setGpsUseTime] = useState(false);
  const [loadingCoords, setLoadingCoords] = useState(false);
  const [loadingTime, setLoadingTime] = useState(false);
  const [loadingGpsSource, setLoadingGpsSource] = useState(false);
  const [loadingMeteoDeviceSource, setLoadingMeteoDeviceSource] = useState(false);
  const [loadingSystemData, setLoadingSystemData] = useState(false);
  const [loadingMeteoSource, setLoadingMeteoSource] = useState(false);
  const [systemData, setSystemData] = useState<GetEspSystemData_GetEspSystemDataResponse | null>(null);
  const [meteoSource, setMeteoSource] = useState<GetMeteoSourceBroadcastDevice_GetMeteoSourceBroadcastDeviceResponse | null>(null);
  const [gpsSourceModalOpen, setGpsSourceModalOpen] = useState(false);
  const [meteoSourceModalOpen, setMeteoSourceModalOpen] = useState(false);
  const [meteoSourceDeviceId, setMeteoSourceDeviceId] = useState<string | null>(null);
  const [meteoSourceLabel, setMeteoSourceLabel] = useState("Не выбрано");
  const [gpsSourceDeviceId, setGpsSourceDeviceId] = useState<string | null>(null);
  const [gpsSourceLabel, setGpsSourceLabel] = useState("Не выбрано");
  const showError = () => {
    toaster.push(
      <Message type="error" showIcon closable>
        Ошибка при сохранении настройки
      </Message>,
      {placement: "topEnd"}
    );
  };

  const showSuccess = () => {
    toaster.push(
      <Message type="success" showIcon closable>
        Настройка сохранена
      </Message>,
      {placement: "topEnd"}
    );
  };

  const formatDeviceLabel = (device: DeviceListItem) => {
    if (device.kind === "coords") {
      return "По координатам";
    }

    const title = device.kind === "clock" ? "Часовой механизм" : "Блок датчиков";
    const model = device.model ? ` ${device.model}` : "";
    const address = device.address ? ` | ${device.address}` : "";

    return `${title}${model}${address}`;
  };

  const formatSourceDeviceLabel = (device?: Common_DeviceDto | null) => {
    if (!device) {
      return "По координатам";
    }

    const isClock =
      device.type === "ClockMechanicE" ||
      device.type === "ClockMechanicN" ||
      device.type === "ClockMechanicV" ||
      device.type === "ElectronicClock";
    const title = isClock ? "Часовой механизм" : "Блок датчиков";
    const model = device.model ? ` ${device.model}` : "";
    const address = device.address != null ? ` | ${device.address}` : "";

    return `${title}${model}${address}`;
  };

  const resolveDeviceSourceLabel = async (
    selectedDeviceId: string | null,
    sourceDevice?: Common_DeviceDto | null
  ) => {
    if (!selectedDeviceId) {
      return "По координатам";
    }

    if (sourceDevice) {
      return formatSourceDeviceLabel(sourceDevice);
    }

    try {
      const response = (await SharedApi.getDevices(deviceId)) as DevicesResponse;
      const {clocks = [], sensors = []} = response || {};
      const devices: DeviceListItem[] = [
        ...clocks.map((item) => ({...item, kind: "clock" as const})),
        ...sensors.map((item) => ({...item, kind: "sensor" as const})),
      ];
      const selectedDevice = devices.find((item) => String(item.id) === selectedDeviceId);

      return selectedDevice ? formatDeviceLabel(selectedDevice) : `Устройство ${selectedDeviceId}`;
    } catch (error) {
      console.error("Ошибка при загрузке списка устройств:", error);
      return `Устройство ${selectedDeviceId}`;
    }
  };

  const loadSystemData = async (options: { updateDeviceSources?: boolean } = {}) => {
    const {updateDeviceSources = true} = options;

    setLoadingSystemData(true);

    try {
      const data = await TestApi.getSystemData(deviceId);
      console.log("[TestComponent] system-data", {
        updateDeviceSources,
        meteoSourceDeviceId: data.meteoSourceDeviceId,
        gpsSourceDeviceId: data.gpsSourceDeviceId,
        data,
      });
      setSystemData(data);
      setGpsUseCoords(Boolean(data.syncCoordinatesFromGps));
      setGpsUseTime(Boolean(data.syncTimeFromGps));

      if (updateDeviceSources) {
        const meteoDeviceId = data.meteoSourceDeviceId ?? null;
        const gpsDeviceId = data.gpsSourceDeviceId ?? null;
        setMeteoSourceDeviceId(meteoDeviceId);
        setMeteoSourceLabel(await resolveDeviceSourceLabel(meteoDeviceId, data.meteoSourceDevice));
        setGpsSourceDeviceId(gpsDeviceId);
        setGpsSourceLabel(await resolveDeviceSourceLabel(gpsDeviceId, data.gpsSourceDevice));
      }
    } catch (error) {
      console.error("Ошибка при загрузке системных данных:", error);
      showError();
    } finally {
      setLoadingSystemData(false);
    }
  };

  const loadMeteoSource = async () => {
    setLoadingMeteoSource(true);

    try {
      const data = await TestApi.getMeteoSource(deviceId);
      console.log("[TestComponent] meteo-source", data);
      setMeteoSource(data);
      const meteoDeviceId = data.deviceId ?? null;
      setMeteoSourceDeviceId(meteoDeviceId);
      setMeteoSourceLabel(await resolveDeviceSourceLabel(meteoDeviceId));
    } catch (error) {
      console.error("Ошибка при загрузке источника метеоданных:", error);
      showError();
    } finally {
      setLoadingMeteoSource(false);
    }
  };

  useEffect(() => {
    loadSystemData().then();
    loadMeteoSource().then();
  }, [deviceId]);

  const handleGpsUseCoordsChange = async (enabled: boolean) => {
    const previousValue = gpsUseCoords;
    setGpsUseCoords(enabled);
    setLoadingCoords(true);

    try {
      await TestApi.updateGpsUseCoords(deviceId, enabled);
      await loadSystemData();
    } catch (error) {
      console.error("Ошибка при обновлении использования GPS координат:", error);
      setGpsUseCoords(previousValue);
      showError();
    } finally {
      setLoadingCoords(false);
    }
  };

  const handleGpsUseTimeChange = async (enabled: boolean) => {
    const previousValue = gpsUseTime;
    setGpsUseTime(enabled);
    setLoadingTime(true);

    try {
      await TestApi.updateGpsUseTime(deviceId, enabled);
      await loadSystemData();
    } catch (error) {
      console.error("Ошибка при обновлении использования GPS времени:", error);
      setGpsUseTime(previousValue);
      showError();
    } finally {
      setLoadingTime(false);
    }
  };

  const handleGpsSourceSelect = async (device: DeviceListItem) => {
    const selectedDeviceId = device.id === "coordinates" ? null : String(device.id);
    const previousDeviceId = gpsSourceDeviceId;
    const previousLabel = gpsSourceLabel;

    console.log("[TestComponent] select gps-source:start", {
      selectedDeviceId,
      previousGpsSourceDeviceId: previousDeviceId,
      currentMeteoSourceDeviceId: meteoSourceDeviceId,
      device,
    });
    setGpsSourceDeviceId(selectedDeviceId);
    setGpsSourceLabel(formatDeviceLabel(device));
    setLoadingGpsSource(true);

    try {
      const result = await TestApi.updateGpsSource(deviceId, selectedDeviceId);
      console.log("[TestComponent] select gps-source:post result", {
        selectedDeviceId,
        result,
      });

      if (!result) {
        throw new Error("Не удалось сохранить GPS source");
      }

      await loadSystemData({updateDeviceSources: false});
      console.log("[TestComponent] select gps-source:done", {
        selectedDeviceId,
        currentMeteoSourceDeviceId: meteoSourceDeviceId,
      });
      showSuccess();
    } catch (error) {
      console.error("Ошибка при обновлении GPS source:", error);
      setGpsSourceDeviceId(previousDeviceId);
      setGpsSourceLabel(previousLabel);
      showError();
    } finally {
      setLoadingGpsSource(false);
    }
  };

  const handleMeteoSourceSelect = async (device: DeviceListItem) => {
    const selectedDeviceId = device.id === "coordinates" ? null : String(device.id);
    const previousDeviceId = meteoSourceDeviceId;
    const previousLabel = meteoSourceLabel;

    console.log("[TestComponent] select meteo-source:start", {
      selectedDeviceId,
      previousMeteoSourceDeviceId: previousDeviceId,
      currentGpsSourceDeviceId: gpsSourceDeviceId,
      device,
    });
    setMeteoSourceDeviceId(selectedDeviceId);
    setMeteoSourceLabel(formatDeviceLabel(device));
    setLoadingMeteoDeviceSource(true);

    try {
      const result = await TestApi.updateMeteoSource(deviceId, selectedDeviceId);
      console.log("[TestComponent] select meteo-source:post result", {
        selectedDeviceId,
        result,
      });

      if (!result) {
        throw new Error("Не удалось сохранить meteo-source");
      }

      await loadSystemData({updateDeviceSources: false});
      await loadMeteoSource();
      console.log("[TestComponent] select meteo-source:done", {
        selectedDeviceId,
        currentGpsSourceDeviceId: gpsSourceDeviceId,
      });
      showSuccess();
    } catch (error) {
      console.error("Ошибка при обновлении meteo-source:", error);
      setMeteoSourceDeviceId(previousDeviceId);
      setMeteoSourceLabel(previousLabel);
      showError();
    } finally {
      setLoadingMeteoDeviceSource(false);
    }
  };

  return (
    <div className="fieldset-wrapper">
      <div className="legend-title">Тестовый раздел</div>
      <div className="row p-2 mt-3">
        <div className="col-md-6 mb-3">
          <Toggle
            color="blue"
            checkedChildren="ON"
            unCheckedChildren="OFF"
            checked={gpsUseCoords}
            loading={loadingCoords}
            disabled={loadingCoords || loadingSystemData}
            onChange={handleGpsUseCoordsChange}
          />
          <span className="radio-title" style={{marginLeft: 15}}>
            Использовать GPS координаты
          </span>
        </div>
        <div className="col-md-6 mb-2">
          <Toggle
            color="blue"
            checkedChildren="ON"
            unCheckedChildren="OFF"
            checked={gpsUseTime}
            loading={loadingTime}
            disabled={loadingTime || loadingSystemData}
            onChange={handleGpsUseTimeChange}
          />
          <span className="radio-title" style={{marginLeft: 15}}>
            Использовать GPS время
          </span>
        </div>
        <div className="col-md-12 mb-2 test-component__device-source">
          <span className="test-component__device-source-label">Метео источник данных:</span>
          <Button
            size="xs"
            appearance="subtle"
            loading={loadingMeteoDeviceSource}
            disabled={loadingMeteoDeviceSource || loadingSystemData}
            title={meteoSourceDeviceId ? `ID: ${meteoSourceDeviceId}` : "По координатам"}
            onClick={() => setMeteoSourceModalOpen(true)}
          >
            {meteoSourceLabel}
          </Button>
        </div>
        <div className="col-md-12 mb-3 test-component__device-source">
          <span className="test-component__device-source-label">GPS источник данных:</span>
          <Button
            size="xs"
            appearance="subtle"
            loading={loadingGpsSource}
            disabled={loadingGpsSource || loadingSystemData}
            title={gpsSourceDeviceId ? `ID: ${gpsSourceDeviceId}` : "По координатам"}
            onClick={() => setGpsSourceModalOpen(true)}
          >
            {gpsSourceLabel}
          </Button>
        </div>
        <div className="col-md-12">
          <div className="mb-2">system-data</div>
          <pre className="mt-0 mb-3 p-3 bg-light rounded">
            {loadingSystemData ? "Загрузка..." : JSON.stringify(systemData, null, 2)}
          </pre>
        </div>
      </div>
      <DevicesModal
        open={gpsSourceModalOpen}
        deviceId={deviceId}
        onClose={() => setGpsSourceModalOpen(false)}
        onSelectDevice={handleGpsSourceSelect}
      />
      <DevicesModal
        open={meteoSourceModalOpen}
        deviceId={deviceId}
        onClose={() => setMeteoSourceModalOpen(false)}
        onSelectDevice={handleMeteoSourceSelect}
      />
    </div>
  );
};

export default TestComponent;
